import { timing } from './params';
import { REFERENCE_PILES, } from './types';
const FEEDBACK_MS = timing.feedbackMs;
const RULES = ['color', 'shape', 'number'];
const COLORS = ['red', 'green', 'yellow', 'blue'];
const SHAPES = ['circle', 'triangle', 'cross', 'star'];
const NUMBERS = [1, 2, 3, 4];
function pickRandom(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}
function pileMatchFor(rule, card) {
    switch (rule) {
        case 'color':
            return REFERENCE_PILES.find((p) => p.card.color === card.color).idx;
        case 'shape':
            return REFERENCE_PILES.find((p) => p.card.shape === card.shape).idx;
        case 'number':
            return REFERENCE_PILES.find((p) => p.card.number === card.number).idx;
    }
}
/** Test helper : compute pileMatch given a rule + card. */
export function computePileMatch(rule, card) {
    return pileMatchFor(rule, card);
}
/**
 * Compose une carte test aleatoire en evitant le degenere ou les 3 dimensions
 * pointent vers la meme pile (ce qui rendrait la regle indetectable).
 */
export function makeTestCard() {
    for (let guard = 0; guard < 32; guard += 1) {
        const card = {
            color: pickRandom(COLORS),
            shape: pickRandom(SHAPES),
            number: pickRandom(NUMBERS),
        };
        const byColor = pileMatchFor('color', card);
        const byShape = pileMatchFor('shape', card);
        const byNumber = pileMatchFor('number', card);
        if (byColor !== byShape || byShape !== byNumber) {
            return card;
        }
    }
    // Fallback : force divergence shape vs color.
    return { color: 'red', shape: 'triangle', number: 2 };
}
export function createWcstAdapter(initialRule = 'color') {
    let state = { subPhase: 'idle', stim: null, feedback: null };
    let currentRule = initialRule;
    let consecCorrect = 0;
    let currentCriterion = 4;
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function shiftRule() {
        const others = RULES.filter((r) => r !== currentRule);
        currentRule = others[Math.floor(Math.random() * others.length)];
        consecCorrect = 0;
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            // Le stim re-derive currentRule + pileMatchIdx au moment present, en cas
            // de build externe : on patche pour rester synchro adapter-truth.
            const patchedStim = {
                ...stim,
                currentRule,
                pileMatchIdx: pileMatchFor(currentRule, stim.testCard),
            };
            currentCriterion = Math.max(1, Math.round(stim.value));
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim: patchedStim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'awaiting_input' });
                    trialStart = Date.now();
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({ subPhase: 'feedback', feedback: correct ? 'correct' : 'incorrect' });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleAnswer(pickedPileIdx) {
            if (state.subPhase !== 'awaiting_input')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const rt = Date.now() - trialStart;
            const correct = pickedPileIdx === stim.pileMatchIdx;
            const ruleAtTime = currentRule;
            if (correct) {
                consecCorrect += 1;
                if (consecCorrect >= currentCriterion) {
                    shiftRule();
                }
            }
            else {
                consecCorrect = 0;
            }
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r({
                    pickedPileIdx,
                    currentRule: ruleAtTime,
                    correct,
                    reactionTimeMs: rt,
                });
            }
        },
        getCurrentRule() {
            return currentRule;
        },
        resetRuleState(initial = 'color') {
            currentRule = initial;
            consecCorrect = 0;
        },
    };
}
//# sourceMappingURL=adapter.js.map